import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useBlocker, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getGameDetailsAPI } from "../../api/game";

export type GameDetailsLayoutGame = {
  current_team_turn?: { team_name?: string; _id?: string } | null;
  [key: string]: unknown;
} | null;

type GameDetailsContextValue = {
  game: GameDetailsLayoutGame;
  isFetching: boolean;
  hasResolved: boolean;
  isAnswerRevealed: boolean;
  setIsAnswerRevealed: (value: boolean) => void;
  isLifelineUsedOnQuestion: boolean;
  setIsLifelineUsedOnQuestion: (value: boolean) => void;
};

const isQuestionScreenPath = (pathname: string) =>
  /\/answer\//.test(pathname) ||
  pathname.includes("backfire-answer") ||
  pathname.includes("1000-question") ||
  pathname.includes("mystery-question");

const GameDetailsContext = createContext<GameDetailsContextValue | null>(null);

function QuestionNavigationBlocker() {
  const location = useLocation();
  const ctx = useContext(GameDetailsContext);

  const isOnQuestionScreen = isQuestionScreenPath(location.pathname);
  const isLifelineUsedForCurrentRound =
    (ctx?.game as { isLifelineUsedForCurrentRound?: boolean } | null)
      ?.isLifelineUsedForCurrentRound ?? false;
  const shouldBlockRouterBack =
    isOnQuestionScreen &&
    (ctx?.isLifelineUsedOnQuestion || isLifelineUsedForCurrentRound);

  const backBlocker = useBlocker(
    ({ historyAction }) => historyAction === "POP" && shouldBlockRouterBack,
  );

  useEffect(() => {
    if (backBlocker.state === "blocked") {
      backBlocker.reset();
    }
  }, [backBlocker, backBlocker.state]);

  return null;
}

export function GameDetailsProvider({ children }: { children: ReactNode }) {
  const { gameId } = useParams<{ gameId?: string }>();
  const location = useLocation();
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);
  const [isLifelineUsedOnQuestion, setIsLifelineUsedOnQuestion] = useState(false);
  const lastGameRef = useRef<GameDetailsLayoutGame>(null);

  useEffect(() => {
    setIsAnswerRevealed(false);
    if (!isQuestionScreenPath(location.pathname)) {
      setIsLifelineUsedOnQuestion(false);
    }
  }, [location.pathname]);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["gameDetails", gameId],
    queryFn: () => getGameDetailsAPI(gameId as string),
    enabled: !!gameId,
    placeholderData: (previousData) => previousData,
    refetchOnMount: "always",
  });

  const latestGame = (data?.data?.game ?? null) as GameDetailsLayoutGame;
  if (latestGame) {
    lastGameRef.current = latestGame;
  }

  const game = (latestGame ?? lastGameRef.current) as GameDetailsLayoutGame;
  const hasResolved = !isLoading || !!game;

  return (
    <GameDetailsContext.Provider
      value={{
        game,
        isFetching,
        hasResolved,
        isAnswerRevealed,
        setIsAnswerRevealed,
        isLifelineUsedOnQuestion,
        setIsLifelineUsedOnQuestion,
      }}
    >
      <QuestionNavigationBlocker />
      {children}
    </GameDetailsContext.Provider>
  );
}

/** Game details loaded once in `GameLayout` for header (and optional reuse). */
export function useGameDetailsFromLayout(): GameDetailsContextValue | null {
  return useContext(GameDetailsContext);
}
